import React, { useState, useEffect, useRef } from 'react';
import './DailySpin.css';

const WHEEL_ITEMS = [
  { id: 'coins', label: 'Monedas', color: '#1a6b1a' },
  { id: 'fuerza', label: 'Fuerza', color: '#8b1a1a' },
  { id: 'defensa', label: 'Defensa', color: '#1a4a8c' },
  { id: 'velocidad', label: 'Velocidad', color: '#8c6b1a' },
  { id: 'precision', label: 'Precisión', color: '#4a1a6b' },
  { id: 'gloves', label: 'Guantes', color: '#6b3a0a' },
  { id: 'feet', label: 'Pads Pie', color: '#0a4a4a' },
  { id: 'head', label: 'Cabezal', color: '#4a0a2a' },
];

// Mapear premio del backend a slot de la rueda
function getPrizeSlotIndex(prize) {
  if (!prize) return 0;
  if (prize.type === 'coins') return 0;
  if (prize.type === 'stat') {
    if (prize.stat === 'fuerza') return 1;
    if (prize.stat === 'defensa') return 2;
    if (prize.stat === 'velocidad') return 3;
    if (prize.stat === 'tecnica') return 4;
    return 1;
  }
  if (prize.type === 'equipment') {
    if (prize.label && prize.label.toLowerCase().includes('guante')) return 5;
    if (prize.label && prize.label.toLowerCase().includes('protector')) return 6;
    if (prize.label && prize.label.toLowerCase().includes('cabezal')) return 7;
    return 5;
  }
  return 0;
}

// Obtener imagen del premio para mostrar en resultado
function getPrizeImage(prize) {
  if (!prize || prize.type !== 'equipment') return null;
  const label = prize.label ? prize.label.toLowerCase() : '';
  
  // Detectar línea
  const lines = ['rose', 'military', 'black', 'raptors'];
  const line = lines.find(l => label.includes(l));
  
  // Detectar nivel básico
  const levels = { 'básic': 'basic', 'pro': 'pro', 'elite': 'elite' };
  let nivel = null;
  for (const [key, val] of Object.entries(levels)) {
    if (label.includes(key)) { nivel = val; break; }
  }
  
  const suffix = line ? `-${line}` : (nivel ? `-${nivel}` : '');
  
  if (label.includes('guante')) return `/assets/equipment/gloves/gloves${suffix}.png`;
  if (label.includes('protector')) return `/assets/equipment/feet/footpads${suffix}.png`;
  if (label.includes('cabezal')) return `/assets/equipment/head/headgear${suffix}.png`;
  return null;
}

function DailySpin({ onClose, onPrizeWon }) {
  const [spinning, setSpinning] = useState(false);
  const [prize, setPrize] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const canvasRef = useRef(null);
  const animRef = useRef(null);

  useEffect(() => {
    drawWheel(0);
  }, []);

  const drawWheel = (currentRotation) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const size = canvas.width;
    const center = size / 2;
    const radius = center - 30;
    const sliceAngle = (2 * Math.PI) / WHEEL_ITEMS.length;

    ctx.clearRect(0, 0, size, size);

    // Sombra exterior
    ctx.save();
    ctx.shadowColor = 'rgba(255, 215, 0, 0.5)';
    ctx.shadowBlur = 30;
    ctx.beginPath();
    ctx.arc(center, center, radius + 18, 0, Math.PI * 2);
    ctx.fillStyle = '#111';
    ctx.fill();
    ctx.restore();

    // Borde dorado exterior
    ctx.beginPath();
    ctx.arc(center, center, radius + 18, 0, Math.PI * 2);
    ctx.strokeStyle = '#FFD700';
    ctx.lineWidth = 4;
    ctx.stroke();

    // Sectores
    ctx.save();
    ctx.translate(center, center);
    ctx.rotate((currentRotation * Math.PI) / 180);
    ctx.translate(-center, -center);

    WHEEL_ITEMS.forEach((item, i) => {
      const startAngle = i * sliceAngle;
      const endAngle = startAngle + sliceAngle;

      const grad = ctx.createRadialGradient(center, center, 0, center, center, radius);
      grad.addColorStop(0, '#222');
      grad.addColorStop(0.3, item.color);
      grad.addColorStop(1, item.color);

      ctx.beginPath();
      ctx.moveTo(center, center);
      ctx.arc(center, center, radius, startAngle, endAngle);
      ctx.closePath();
      ctx.fillStyle = grad;
      ctx.fill();

      ctx.strokeStyle = 'rgba(255, 215, 0, 0.5)';
      ctx.lineWidth = 2;
      ctx.stroke();

      // Texto
      ctx.save();
      ctx.translate(center, center);
      ctx.rotate(startAngle + sliceAngle / 2);
      ctx.textAlign = 'center';
      ctx.fillStyle = '#fff';
      ctx.shadowColor = '#000';
      ctx.shadowBlur = 4;
      ctx.font = 'bold 14px Arial';
      ctx.fillText(item.label, radius * 0.6, 5);
      ctx.restore();
    });

    ctx.restore();

    // Centro
    const centerGrad = ctx.createRadialGradient(center, center, 0, center, center, 35);
    centerGrad.addColorStop(0, '#FFD700');
    centerGrad.addColorStop(0.7, '#B8860B');
    centerGrad.addColorStop(1, '#8B6914');
    ctx.beginPath();
    ctx.arc(center, center, 32, 0, Math.PI * 2);
    ctx.fillStyle = centerGrad;
    ctx.fill();
    ctx.strokeStyle = '#FFD700';
    ctx.lineWidth = 3;
    ctx.stroke();

    ctx.fillStyle = '#1a1a2e';
    ctx.font = 'bold 14px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('TKD', center, center - 6);
    ctx.font = 'bold 9px Arial';
    ctx.fillText('MASTER', center, center + 8);

    // Flecha arriba
    ctx.save();
    ctx.shadowColor = '#FF4444';
    ctx.shadowBlur = 10;
    ctx.beginPath();
    ctx.moveTo(center - 14, 8);
    ctx.lineTo(center + 14, 8);
    ctx.lineTo(center, 35);
    ctx.closePath();
    ctx.fillStyle = '#FF4444';
    ctx.fill();
    ctx.strokeStyle = '#FFD700';
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.restore();
  };

  const handleSpin = async () => {
    if (spinning) return;
    setSpinning(true);

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/profile/daily-spin`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await res.json();

      if (!res.ok) {
        setPrize({ label: data.error || 'Error', icon: '❌' });
        setShowResult(true);
        setSpinning(false);
        return;
      }

      // Calcular rotación para que frene en el slot correcto
      const targetIndex = getPrizeSlotIndex(data.prize);
      const sliceAngle = 360 / WHEEL_ITEMS.length;
      // La flecha está arriba (270°), calcular ángulo para que el slot quede ahí
      const targetAngle = 360 - (targetIndex * sliceAngle + sliceAngle / 2) + 270;
      const fullSpins = 2160; // 6 vueltas completas
      const totalRotation = fullSpins + (targetAngle % 360) + (Math.random() * sliceAngle * 0.4 - sliceAngle * 0.2);

      const duration = 5000;
      const start = Date.now();

      const animate = () => {
        const elapsed = Date.now() - start;
        const progress = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 4);
        const current = eased * totalRotation;
        drawWheel(current);

        if (progress < 1) {
          animRef.current = requestAnimationFrame(animate);
        } else {
          setPrize(data.prize);
          setShowResult(true);
          setSpinning(false);
          if (onPrizeWon) onPrizeWon(data.prize);
        }
      };

      animRef.current = requestAnimationFrame(animate);
    } catch (error) {
      console.error('Error al girar:', error);
      setPrize({ label: 'Error de conexión', icon: '❌' });
      setShowResult(true);
      setSpinning(false);
    }
  };

  useEffect(() => {
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, []);

  return (
    <div className="daily-spin-overlay">
      <div className="daily-spin-modal">
        {[...Array(15)].map((_, i) => <div key={i} className="spin-ash" />)}

        <h2 className="spin-title">🎰 RULETA DIARIA</h2>
        <p className="spin-subtitle">¡Girá una vez por día y ganá premios!</p>

        <div className="wheel-wrapper">
          <canvas ref={canvasRef} width={320} height={320} className="spin-wheel" />
        </div>

        {!showResult ? (
          <button
            className={`spin-btn ${spinning ? 'spinning' : ''}`}
            onClick={handleSpin}
            disabled={spinning}
          >
            {spinning ? '🎰 GIRANDO...' : '¡GIRAR!'}
          </button>
        ) : (
          <div className="spin-result">
            <div className="result-glow"></div>
            {getPrizeImage(prize) ? (
              <img src={getPrizeImage(prize)} alt={prize?.label} className="result-prize-img" />
            ) : (
              <div className="result-icon">{prize?.icon}</div>
            )}
            <p className="result-label">¡GANASTE!</p>
            <p className="result-prize">{prize?.label}</p>
            <button className="spin-btn collect" onClick={onClose}>RECOGER PREMIO</button>
          </div>
        )}
      </div>
    </div>
  );
}

export default DailySpin;
